// Melody PHRASING for a swing line (docs/swing-plan.md §4.4): the written
// rhythm played the way every method teaches a horn player to swing it. Pure:
// the band hook runs it over the melody track before the player sees it.
//
// A written eighth-note line played as written (every note 98% legato, level
// even) is the "stilted" melody the user heard. The bebop articulation that
// goes with swing is a matter of length and weight:
//
//   - in a run of eighths, the downbeat eighth is legato INTO the next note
//     and slightly lighter, the upbeat eighth a touch detached and lighter
//     still on the tongue but accented (slur the downbeat, tongue the upbeat);
//   - a phrase's last note on an upbeat is short and accented (the "bop"), on
//     a downbeat shorter than a half note it is clipped, and a long note keeps
//     its length and its release;
//   - quarters in a swing line are a little detached;
//   - the phrase's level rises toward its highest note and eases off after it.
//
// A written articulation (a staccato, a tenuto) or a written dynamic wins over
// all of this, as it does everywhere else, and nothing changes where the band
// plays straight or the line moves finer than the swing (a pair a part plays
// in sixteenths under an eighth feel stays straight, swingFeel.ts).

import { straightPairs, type SwingGrid } from '@/music/swingFeel';

export interface PhrasedNote {
  beat: number;
  durationBeats: number;
  midi: number;
  velocity?: number;
  articulationGate?: number;
}

export interface PhrasingOptions {
  /** Whether the band swings at `beat`, and with phrasing on. */
  swingsAt: (beat: number) => boolean;
  /** The swing level at `beat`: 1 for eighths, 0.5 for sixteenths. */
  pairAt?: (beat: number) => number;
  /** Where the beat grid starts (a fractional pickup moves every beat). */
  origin?: number;
}

const GRID = 0.02;

/** Gates and weights, each from the methods' description (see the header). */
const RUN_DOWNBEAT = { gate: 1, weight: 0.97 };
const RUN_UPBEAT = { gate: 0.9, weight: 1.05 };
const END_UPBEAT = { gate: 0.55, weight: 1.08 };
const END_DOWNBEAT_GATE = 0.8;
const QUARTER_GATE = 0.92;
/** The phrase contour: from this much below the peak, easing to END after it. */
const CONTOUR_START = 0.88;
const CONTOUR_END = 0.9;
const LEVEL_CAP = 1.1;

function near(a: number, b: number): boolean {
  return Math.abs(a - b) < GRID;
}

/**
 * The line phrased. Notes keep their onsets and written lengths; what changes
 * is how much of each sounds (`articulationGate`) and how loud (`velocity`).
 */
export function phraseMelody<T extends PhrasedNote>(notes: T[], opts: PhrasingOptions): T[] {
  if (notes.length === 0) return notes;
  const origin = opts.origin ?? 0;
  const pairAt = opts.pairAt ?? (() => 1);
  const order = notes
    .map((note, index) => ({ note, index }))
    .sort((a, b) => a.note.beat - b.note.beat);
  // Pairs the line plays finer than each level, judged on the line itself.
  const straightByPair = new Map<number, Set<number>>();
  const straightFor = (pair: number) => {
    let set = straightByPair.get(pair);
    if (!set) {
      const grid: SwingGrid = { pair, ratio: 2 / 3 };
      set = straightPairs(
        notes.map((n) => n.beat - origin),
        grid,
      );
      straightByPair.set(pair, set);
    }
    return set;
  };

  // Phrases: runs of notes with no rest of half a pair or more between them.
  const phrases: number[][] = [];
  let current: number[] = [];
  for (let k = 0; k < order.length; k++) {
    const { note } = order[k];
    const prev = order[k - 1]?.note;
    const gap = prev ? note.beat - (prev.beat + prev.durationBeats) : Infinity;
    if (current.length && gap >= pairAt(note.beat) / 2 - GRID) {
      phrases.push(current);
      current = [];
    }
    current.push(k);
  }
  if (current.length) phrases.push(current);

  const out = notes.slice();
  for (const phrase of phrases) {
    // The phrase's highest note, its first occurrence: the contour's peak.
    let peak = 0;
    for (let p = 1; p < phrase.length; p++) {
      if (order[phrase[p]].note.midi > order[phrase[peak]].note.midi) peak = p;
    }
    for (let p = 0; p < phrase.length; p++) {
      const k = phrase[p];
      const { note, index } = order[k];
      if (!opts.swingsAt(note.beat)) continue;
      const pair = pairAt(note.beat);
      const half = pair / 2;
      const position = note.beat - origin;
      const pairIndex = Math.floor(position / pair + 1e-9);
      const within = position - pairIndex * pair;
      if (straightFor(pair).has(pairIndex)) continue;
      const onDownbeat = near(within, 0) || near(within, pair);
      const onUpbeat = near(within, half);
      const next = order[k + 1]?.note;
      const last = p === phrase.length - 1;
      const short = near(note.durationBeats, half);
      const quarter = near(note.durationBeats, pair);
      const connected = !!next && near(next.beat, note.beat + note.durationBeats);

      let gate: number | undefined;
      let weight = 1;
      if (last) {
        if (onUpbeat && note.durationBeats <= pair + GRID) {
          gate = END_UPBEAT.gate;
          weight = END_UPBEAT.weight;
        } else if (onDownbeat && note.durationBeats < 2 * pair - GRID) {
          gate = END_DOWNBEAT_GATE;
        }
      } else if (short && connected && onDownbeat) {
        gate = RUN_DOWNBEAT.gate;
        weight = RUN_DOWNBEAT.weight;
      } else if (short && connected && onUpbeat) {
        gate = RUN_UPBEAT.gate;
        weight = RUN_UPBEAT.weight;
      } else if (quarter && connected) {
        gate = QUARTER_GATE;
      }

      // The contour: rising to the phrase's highest note, easing off after it.
      const contour =
        phrase.length < 2
          ? 1
          : p <= peak
            ? peak === 0
              ? 1
              : CONTOUR_START + (1 - CONTOUR_START) * (p / peak)
            : 1 - (1 - CONTOUR_END) * ((p - peak) / (phrase.length - 1 - peak));
      const lengthLift = note.durationBeats >= pair ? 1.03 : 1;

      const phrased: T = { ...note };
      // A written articulation or dynamic is what the score asks for, exactly.
      if (gate !== undefined && note.articulationGate === undefined) {
        phrased.articulationGate = gate;
      }
      if (note.velocity === undefined) {
        phrased.velocity = Math.min(LEVEL_CAP, weight * contour * lengthLift);
      }
      out[index] = phrased;
    }
  }
  return out;
}
